import { useEffect, useState } from 'react';
import { useToast } from '@/contexts/ToastContext';
import { scrapApi, num, type ScrapEntry, type DisposalMethod } from '@/api/scrap.api';
import { Modal, Field, inputCls, inr, qtyText, noNegativeKeys } from './scrapUi';

// Dispose of scrap held in the yard: sell it (buyer, amount, GST, cash / bank), destroy it or donate it.
// Confirming posts the disposal to the ledger.

const METHODS: { value: DisposalMethod; label: string; icon: string }[] = [
  { value: 'SOLD', label: 'Sell', icon: 'ri-hand-coin-line' },
  { value: 'DESTROYED', label: 'Destroy', icon: 'ri-fire-line' },
  { value: 'DONATED', label: 'Donate', icon: 'ri-heart-line' },
];
const GST_RATES = [0, 5, 12, 18, 28];
const today = () => new Date().toISOString().slice(0, 10);

interface Props {
  entry: ScrapEntry | null;
  onClose: () => void;
  onDone: (entry: ScrapEntry) => void;
}

export default function DisposeScrapModal({ entry, onClose, onDone }: Props) {
  const toast = useToast();
  const [method, setMethod] = useState<DisposalMethod>('SOLD');
  const [buyer, setBuyer] = useState('');
  const [amount, setAmount] = useState('');
  const [gstRate, setGstRate] = useState(18);
  const [gstType, setGstType] = useState<'INTRA' | 'INTER'>('INTRA');
  const [paymentMode, setPaymentMode] = useState<'BANK' | 'CASH'>('BANK');
  const [date, setDate] = useState(today());
  const [cost, setCost] = useState('');
  const [reference, setReference] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!entry) return;
    setMethod('SOLD');
    setBuyer('');
    setAmount('');
    setGstRate(18);
    setGstType('INTRA');
    setPaymentMode('BANK');
    setDate(today());
    setCost('');
    setReference('');
    setErrors({});
  }, [entry]);

  if (!entry) return null;

  const held = num(entry.scrap_value);
  const book = num(entry.book_value);
  const sale = method === 'SOLD' ? Number(amount) || 0 : 0;
  const gst = Math.round(sale * gstRate) / 100;
  const disposalCost = Number(cost) || 0;
  const result = sale - held - disposalCost;

  const submit = async () => {
    const e: Record<string, string> = {};
    if (method === 'SOLD') {
      if (!buyer.trim()) e.buyer = 'Enter the buyer';
      if (!(sale > 0)) e.amount = 'Enter the sale amount';
    }
    if (cost !== '' && !(disposalCost >= 0)) e.cost = 'Enter 0 or more';
    setErrors(e);
    if (Object.keys(e).length) return;
    setSaving(true);
    try {
      const res = await scrapApi.dispose(entry.id, {
        method,
        disposalDate: date,
        buyerName: buyer.trim() || undefined,
        saleAmount: sale,
        gstRate: method === 'SOLD' ? gstRate : 0,
        gstType,
        disposalCost,
        paymentMode,
        reference: reference.trim() || undefined,
      });
      toast.success(res.message || 'Scrap disposed');
      onDone(res.data);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not dispose of the scrap');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={`Dispose Scrap — ${entry.item_name} (${qtyText(entry.qty)} ${entry.unit || ''})`}
      subtitle={`${entry.scrap_number} · ${entry.scrap_location}`}
      onClose={onClose}
      footer={(
        <>
          <button type="button" onClick={onClose} className="h-9 px-4 rounded-lg border border-[#e2e8f0] text-sm font-medium text-[#64748b] hover:bg-white cursor-pointer">Cancel</button>
          <button type="button" onClick={() => void submit()} disabled={saving}
            className="h-9 px-4 rounded-lg bg-[#4f46e5] text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60 cursor-pointer flex items-center gap-2">
            {saving ? <><i className="ri-loader-4-line animate-spin" /> Posting…</> : <><i className="ri-check-line" /> Confirm Disposal</>}
          </button>
        </>
      )}
    >
      <div className="grid grid-cols-2 gap-3 text-xs">
        <div className="rounded-lg border border-[#e2e8f0] px-3 py-2">
          <p className="text-[#94a3b8]">Held in scrap stock at</p>
          <p className="text-base font-bold text-[#1e293b]">{inr(held)}</p>
        </div>
        <div className="rounded-lg border border-[#e2e8f0] px-3 py-2">
          <p className="text-[#94a3b8]">Original material cost</p>
          <p className="text-base font-bold text-[#64748b]">{inr(book)}</p>
        </div>
      </div>

      <Field label="Disposal Method" required>
        <div className="grid grid-cols-3 gap-2">
          {METHODS.map((m) => (
            <button key={m.value} type="button" onClick={() => setMethod(m.value)}
              className={`h-10 rounded-lg border text-sm font-semibold flex items-center justify-center gap-1.5 cursor-pointer transition-colors ${method === m.value ? 'border-[#4f46e5] bg-indigo-50 text-[#4f46e5]' : 'border-[#e2e8f0] text-[#64748b] hover:border-[#4f46e5]/40'}`}>
              <i className={m.icon} /> {m.label}
            </button>
          ))}
        </div>
      </Field>

      {method === 'SOLD' && (
        <>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Buyer Name" required error={errors.buyer}>
              <input type="text" value={buyer} onChange={(e) => setBuyer(e.target.value)} placeholder="e.g. Rajesh Kabadi" className={inputCls} />
            </Field>
            <Field label="Sale Amount (₹, before GST)" required error={errors.amount}>
              <input type="number" min="0" step="any" value={amount} onKeyDown={noNegativeKeys}
                onChange={(e) => setAmount(e.target.value.replace(/-/g, ''))} className={inputCls} placeholder="0" />
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="GST Rate">
              <select value={gstRate} onChange={(e) => setGstRate(Number(e.target.value))} className={inputCls}>
                {GST_RATES.map((r) => <option key={r} value={r}>{r}%</option>)}
              </select>
            </Field>
            <Field label="Supply">
              <select value={gstType} onChange={(e) => setGstType(e.target.value as 'INTRA' | 'INTER')} className={inputCls}>
                <option value="INTRA">Within state (CGST + SGST)</option>
                <option value="INTER">Other state (IGST)</option>
              </select>
            </Field>
            <Field label="Received In">
              <select value={paymentMode} onChange={(e) => setPaymentMode(e.target.value as 'BANK' | 'CASH')} className={inputCls}>
                <option value="BANK">Bank</option>
                <option value="CASH">Cash</option>
              </select>
            </Field>
          </div>
        </>
      )}

      <div className="grid grid-cols-3 gap-3">
        <Field label="Date" required>
          <input type="date" value={date} max={today()} onChange={(e) => setDate(e.target.value)} className={inputCls} />
        </Field>
        <Field label={method === 'SOLD' ? 'Disposal / Carting Cost (₹)' : 'Destruction Cost (₹)'} error={errors.cost}>
          <input type="number" min="0" step="any" value={cost} onKeyDown={noNegativeKeys}
            onChange={(e) => setCost(e.target.value.replace(/-/g, ''))} className={inputCls} placeholder="0" />
        </Field>
        <Field label="Reference">
          <input type="text" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Bill / receipt no." className={inputCls} />
        </Field>
      </div>
      {method !== 'SOLD' && disposalCost > 0 && (
        <Field label="Paid From">
          <select value={paymentMode} onChange={(e) => setPaymentMode(e.target.value as 'BANK' | 'CASH')} className={inputCls}>
            <option value="BANK">Bank</option>
            <option value="CASH">Cash</option>
          </select>
        </Field>
      )}

      {/* Net result, as it will be posted */}
      <div className={`rounded-lg border px-3 py-2.5 text-xs space-y-1 ${result >= 0 ? 'border-green-200 bg-green-50/60' : 'border-red-200 bg-red-50/60'}`}>
        {method === 'SOLD' ? (
          <>
            <div className="flex justify-between"><span className="text-[#64748b]">Sale amount</span><span>{inr(sale)}</span></div>
            {gst > 0 && <div className="flex justify-between"><span className="text-[#64748b]">GST {gstRate}% ({gstType === 'INTER' ? 'IGST' : 'CGST + SGST'}) — collected for the government</span><span>{inr(gst)}</span></div>}
            <div className="flex justify-between"><span className="text-[#64748b]">Total to receive</span><strong>{inr(sale + gst)}</strong></div>
          </>
        ) : (
          <div className="flex justify-between"><span className="text-[#64748b]">Scrap value written off</span><span>{inr(held)}</span></div>
        )}
        <div className={`flex justify-between pt-1 border-t ${result >= 0 ? 'border-green-200' : 'border-red-200'}`}>
          <span className="font-semibold text-[#1e293b]">Net result</span>
          <strong className={result >= 0 ? 'text-green-700' : 'text-red-600'}>
            {result >= 0 ? 'Profit' : 'Loss'} {inr(Math.abs(result))}
            <span className="font-normal text-[#64748b]"> ({inr(sale)} − {inr(held)} held{disposalCost ? ` − ${inr(disposalCost)} cost` : ''})</span>
          </strong>
        </div>
        {method === 'SOLD' && book > 0 && (
          <p className="text-[#94a3b8]">Against the original cost of {inr(book)}: {sale >= book ? 'recovered in full' : `${inr(book - sale)} not recovered`} ({Math.round((sale / book) * 100)}% recovered).</p>
        )}
      </div>
    </Modal>
  );
}
